import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiClientService } from '../../../core/http/api-client.service';
import {
  ConfigureTelegramWebhookRequest,
  TelegramLinkDto,
  TelegramLinkTokenDto,
  TelegramSettingsDto,
  UpdateTelegramSettingsRequest,
} from './telegram-connector.models';

@Injectable({
  providedIn: 'root',
})
export class TelegramConnectorService {
  private readonly apiClient = inject(ApiClientService);

  public getSettings(companyId?: string): Observable<TelegramSettingsDto | null> {
    return this.apiClient.get<TelegramSettingsDto | null>(
      '/connectors/telegram/settings',
      companyId ? { companyId } : undefined,
    );
  }

  public updateSettings(
    payload: UpdateTelegramSettingsRequest,
    companyId?: string,
  ): Observable<TelegramSettingsDto> {
    return this.apiClient.patch<TelegramSettingsDto, UpdateTelegramSettingsRequest>(
      '/connectors/telegram/settings',
      payload,
      companyId ? { companyId } : undefined,
    );
  }

  public validateSettings(companyId?: string): Observable<TelegramSettingsDto> {
    return this.apiClient.post<TelegramSettingsDto>(
      '/connectors/telegram/settings/validate',
      {},
      companyId ? { companyId } : undefined,
    );
  }

  public configureWebhook(
    payload: ConfigureTelegramWebhookRequest,
    companyId?: string,
  ): Observable<TelegramSettingsDto> {
    return this.apiClient.post<TelegramSettingsDto, ConfigureTelegramWebhookRequest>(
      '/connectors/telegram/settings/webhook',
      payload,
      companyId ? { companyId } : undefined,
    );
  }

  public getMyLink(companyId?: string): Observable<TelegramLinkDto> {
    return this.apiClient.get<TelegramLinkDto>(
      '/connectors/telegram/link/me',
      companyId ? { companyId } : undefined,
    );
  }

  public createMyLinkToken(companyId?: string): Observable<TelegramLinkTokenDto> {
    return this.apiClient.post<TelegramLinkTokenDto>(
      '/connectors/telegram/link/me/token',
      {},
      companyId ? { companyId } : undefined,
    );
  }

  public revokeMyLink(companyId?: string): Observable<TelegramLinkDto> {
    return this.apiClient.delete<TelegramLinkDto>(
      '/connectors/telegram/link/me',
      companyId ? { companyId } : undefined,
    );
  }

  public getMyClientLink(): Observable<TelegramLinkDto> {
    return this.apiClient.get<TelegramLinkDto>('/connectors/telegram/link/client/me');
  }

  public createMyClientLinkToken(): Observable<TelegramLinkTokenDto> {
    return this.apiClient.post<TelegramLinkTokenDto>(
      '/connectors/telegram/link/client/me/token',
      {},
    );
  }

  public revokeMyClientLink(): Observable<TelegramLinkDto> {
    return this.apiClient.delete<TelegramLinkDto>('/connectors/telegram/link/client/me');
  }

  public getClientLink(clientId: string, companyId?: string): Observable<TelegramLinkDto> {
    return this.apiClient.get<TelegramLinkDto>(
      `/connectors/telegram/link/clients/${encodeURIComponent(clientId)}`,
      companyId ? { companyId } : undefined,
    );
  }

  public createClientLinkToken(
    clientId: string,
    companyId?: string,
  ): Observable<TelegramLinkTokenDto> {
    return this.apiClient.post<TelegramLinkTokenDto>(
      `/connectors/telegram/link/clients/${encodeURIComponent(clientId)}/token`,
      {},
      companyId ? { companyId } : undefined,
    );
  }

  public revokeClientLink(clientId: string, companyId?: string): Observable<TelegramLinkDto> {
    return this.apiClient.delete<TelegramLinkDto>(
      `/connectors/telegram/link/clients/${encodeURIComponent(clientId)}`,
      companyId ? { companyId } : undefined,
    );
  }
}
